import {
    useCallback, useMemo, useState,
} from 'react'
import AddIcon from '@mui/icons-material/Add'

import Card from 'app/shared-components/Card'
import useLabel from 'app/hooks/useLabel'
import requests from 'app/requests/Order/PoolingDashboard.requests'
import prebookingRequests from 'app/requests/Prebooking.requests'
import {
    TableControlTypes,
} from 'app/shared-components/TableControls'
import SkyNetTable from 'app/shared-components/SkyNetTable'
import Button from 'app/shared-components/Button'
import {
    RouteDescription, RoutesConfig,
} from 'app/SkyNetRoutes/SkyNetRoutes.types'
import {
    LeaseType,
} from 'app/enums/label.enums'
import {
    YesNo,
} from 'app/enums/common.enums'
import Radio from 'app/shared-components/Radio'

import useOrderManagementRoutes from 'app/SkyNetRoutes/hooks/useOrderManagementRoutes'

import useStyles from './Pooling.style'
import getDirectLeaseConfig from './configs/directLease.config'
import internalTransportsConfig from './configs/internalTransports.config'
import containerSelectionConfig from './configs/containerSelection.config'
import getAirlineLeaseConfig from './configs/airlineLease.config'
import getPickupAddInfoConfig from './configs/pickupAddInfo.config'
import getPickupAddPickUpConfig from './configs/pickupAddPickUp.config'
import getPickupDeliveryConfirmationConfig from './configs/pickupDeliveryConfirmation.config'
import getTransportDepartureConfig from './configs/transportDeparture.config'
import transportArrivalConfig from './configs/transportArrival.config'
import getDeliveryAddInfoConfig from './configs/deliveryAddInfo.config'
import getDeliveryAddPickupConfig from './configs/deliveryAddPickup.config'
import getDeliveryConfirmationConfig from './configs/deliveryConfirmation.config'
import preBookings from './configs/preBookings.config'

const tableControls: TableControlTypes[] = [
    TableControlTypes.ClearFilters,
    TableControlTypes.ClearSorting,
]

const filterOptions: {
    label: string,
    value: boolean,
}[] = [
    {
        value: true,
        label: YesNo.YES,
    },
    {
        value: false,
        label: YesNo.NO,
    },
]

const Pooling = () => {
    const Routes = useOrderManagementRoutes()
    const {
        classes,
    } = useStyles()

    const l = useLabel()

    const [
        isOverdueFilter,
        setIsOverdueFilter,
    ] = useState<boolean>(true)

    const openCreateForm = useCallback(
        ({
            collectionDropoffPointId, selectedPreviousOrderId,
        }: {
            collectionDropoffPointId?: number,
            selectedPreviousOrderId?: number,
        } = {}): void => {
            Routes.InternalOrders.Create.navigate({
                options: {
                    state: {
                        collectionDropoffPointId,
                        selectedPreviousOrderId,
                    },
                },
            })
        },
        [Routes],
    )

    const onRowClick = useCallback(
        (
            rout: RouteDescription,
            {
                orderNumber,
            }: {
                orderNumber: string;
            },
        ): void => {
            rout.navigate({
                params: {
                    key: orderNumber,
                },
                options: {
                    state: {
                        filter: {
                            isOverdue: isOverdueFilter,
                        },
                    },
                },
            })
        },
        [isOverdueFilter],
    )

    const onRowClickPreBookings = ({
        id,
    }: { id: number }): void => {
        Routes.PreBookings.Overview.navigate({
            params: {
                key: id,
            },
        })
    }

    const {
        directLeaseConfig,
        airlineLeaseConfig,
        pickupAddInfoConfig,
        pickupAddPickUpConfig,
        pickupDeliveryConfirmationConfig,
        transportDepartureConfig,
        deliveryAddInfoConfig,
        deliveryAddPickupConfig,
        deliveryConfirmationConfig,
    } = useMemo(() => {
        return {
            directLeaseConfig: getDirectLeaseConfig(openCreateForm),
            airlineLeaseConfig: getAirlineLeaseConfig(openCreateForm),
            pickupAddInfoConfig: getPickupAddInfoConfig(isOverdueFilter),
            pickupAddPickUpConfig: getPickupAddPickUpConfig(isOverdueFilter),
            pickupDeliveryConfirmationConfig: getPickupDeliveryConfirmationConfig(isOverdueFilter),
            transportDepartureConfig: getTransportDepartureConfig(isOverdueFilter),
            deliveryAddInfoConfig: getDeliveryAddInfoConfig(isOverdueFilter),
            deliveryAddPickupConfig: getDeliveryAddPickupConfig(isOverdueFilter),
            deliveryConfirmationConfig: getDeliveryConfirmationConfig(isOverdueFilter),
        }
    }, [
        isOverdueFilter,
        openCreateForm,
    ])

    return (
        <div className={classes.root}>
            <div className={classes.headerTitle}>
                Container Selection
                <div className={classes.actionWrapper}>
                    <Button
                        label="Create Internal Order"
                        onClick={openCreateForm}
                        data-testid="openCreateFormBtn"
                        icon={<AddIcon />}
                    />
                </div>
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingContainerSelectionView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            Routes.InternalOrders.ContainerSelection,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.poolingContainerSelection().url}
                    tableConfig={containerSelectionConfig}
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                />
            </div>
            <div className={classes.headerTitle}>Pre Bookings</div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingPreBookingsTransportsView"
                    tableControls={tableControls}
                    onRowClick={onRowClickPreBookings}
                    customUrl={
                        prebookingRequests.filterRequestContainerAvailabilityAssessment().url
                    }
                    tableConfig={preBookings}
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                    data-testid="preBookingsTransports"
                />
            </div>
            <div className={classes.headerTitle}>Customer Transports</div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingCustomerTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(Routes.CustomerOrders.OrderSteps, args)
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.poolingCustomerTransports().url}
                    filter={{
                        orderSubType: [LeaseType.DIRECT_LEASE],
                    }}
                    tableConfig={directLeaseConfig}
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingCustomerTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(Routes.CustomerOrders.OrderSteps, args)
                    }}
                    uniqField="orderNumber"
                    customUrl={
                        requests.poolingCustomerTransports().url
                    }
                    filter={{
                        orderSubType: [LeaseType.AIRLINE_LEASE],
                    }}
                    tableConfig={airlineLeaseConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
            <div className={classes.headerTitle}>Internal Transports</div>
            <Card
                title="Filter by"
                className={classes.filterWrapper}
            >
                <Radio
                    title={l('Order.IsDelayed')}
                    value={isOverdueFilter}
                    options={filterOptions}
                    compact
                    onChange={setIsOverdueFilter}
                />
            </Card>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            (Routes.InternalOrders.PickupAddInfo as RoutesConfig).OrderSteps,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.pickupAddInfo().url}
                    tableConfig={pickupAddInfoConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            (Routes.InternalOrders.PickupAddPickup as RoutesConfig).OrderSteps,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.poolingPickupAddPickup().url}
                    tableConfig={pickupAddPickUpConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            (Routes.InternalOrders.PickupDeliveryConfirmation as RoutesConfig)
                                .OrderSteps,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={
                        requests.poolingPickupDeliveryConfirmation().url
                    }
                    tableConfig={pickupDeliveryConfirmationConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            (Routes.InternalOrders.TransportDeparture as RoutesConfig).OrderSteps,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.poolingTransportDeparture().url}
                    tableConfig={transportDepartureConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            (Routes.InternalOrders.TransportArrival as RoutesConfig).OrderSteps,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.poolingTransportArrival().url}
                    tableConfig={transportArrivalConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            (Routes.InternalOrders.DeliveryAddInfo as RoutesConfig).OrderSteps,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.poolingDeliveryAddInfo().url}
                    tableConfig={deliveryAddInfoConfig}
                    isShowCounterTab
                    showSearch
                    paginationModeMatches
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            (Routes.InternalOrders.DeliveryAddPickup as RoutesConfig).OrderSteps,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.poolingDeliveryAddPickup().url}
                    tableConfig={deliveryAddPickupConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(
                            (Routes.InternalOrders.DeliveryConfirmation as RoutesConfig).OrderSteps,
                            args,
                        )
                    }}
                    uniqField="orderNumber"
                    customUrl={requests.poolingDeliveryConfirmation().url}
                    tableConfig={deliveryConfirmationConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
            <div className={classes.headerTitle}>Open Transports</div>
            <div className={classes.tableWrapper}>
                <SkyNetTable
                    name="OrderPoolingInternalTransportsView"
                    tableControls={tableControls}
                    onRowClick={(args) => {
                        onRowClick(Routes.InternalOrders.OrderSteps, args)
                    }}
                    uniqField="orderNumber"
                    customUrl={
                        requests.poolingInternalTransports().url
                    }
                    tableConfig={internalTransportsConfig}
                    isShowCounterTab
                    paginationModeMatches
                    showSearch
                />
            </div>
        </div>
    )
}

export default Pooling
